import { FieldType } from 'onefaceui-react-components';
import moment from 'moment';
import { convertHourToSeconds, isDate, isTime } from './onefaceLibraryUtils';

const matchModes = {
  EXACT: 'EXACT',
  ANYWHERE: 'ANYWHERE',
  START: 'START',
  END: 'END'
};

const removeIdentifierMatchModeOfValue = (value) => {
  if (!value) return '';

  return value.replace(/(?:^\*)|(?:\*$)/g, '');
};

const getFieldRangeByType = (type) => {
  if (type === FieldType.DATETIME || type === FieldType.DATE) {
    return FieldType.DATE_RANGE;
  }

  if (type === FieldType.DECIMAL) {
    return FieldType.DECIMAL_RANGE;
  }

  if (type === FieldType.NUMBER) {
    return FieldType.NUMBER_RANGE;
  }

  if (type === FieldType.TIME) {
    return FieldType.TIME_RANGE;
  }

  return null;
};

const isAIntervalField = (field) =>
  field.name.split('_')?.[1] === 'range' &&
  field?.identifyName &&
  field.generateRangeField;

const parseFields = (fields) => {
  const newFields = [...fields];
  const fieldsWithRange = fields.filter((field) => field.generateRangeField);

  fieldsWithRange.forEach((field) => {
    const type = getFieldRangeByType(field.type);

    if (type) {
      newFields.push({
        ...field,
        identifyName: field.name,
        name: `${field.name}_range`
      });
    }
  });

  return newFields;
};

const getAttributeKey = (field) => {
  if (field.type === FieldType.LOOKUP && field.lookupParams) {
    return `${field.name}.${field.lookupParams?.column}`;
  }

  if (field.type === FieldType.LOOKUP_MULTISELECT && field?.meta) {
    const meta = JSON.parse(field.meta || '{}');

    return meta?.hasMany?.criteriaChainAttribute;
  }

  if (isAIntervalField(field)) {
    return field.identifyName;
  }

  if (field.type === FieldType.MULTIPLE_SELECT) {
    return `${field.name}.id`;
  }

  return field.name;
};

const isATextValue = (field, isLookupFilter) => {
  if (isLookupFilter) {
    return [FieldType.TEXT].includes(field?.type);
  }

  return [FieldType.TEXT].includes(field?.type);
};

const getMatchMode = (value) => {
  let matchMode = matchModes.EXACT;

  if (value) {
    const firstChar = value[0];
    const lastChar = value.slice(-1);

    if (firstChar === '*' && lastChar === '*') {
      matchMode = matchModes.ANYWHERE;
    } else if (firstChar === '*') {
      matchMode = matchModes.END;
    } else if (lastChar === '*') {
      matchMode = matchModes.START;
    }
  }

  return matchMode;
};

export const getCriteriaChain = (
  filters = {},
  fields,
  isLookupFilter = false,
  sort = []
) => {
  const criteriaChain = {
    criteriaChain: [
      {
        and: []
      }
    ],
    sort
  };

  const fieldsWithInterval = parseFields(fields);
  const parseDateOrTime = (val, isSameValue = false) => {
    if (isDate(val)) {
      return isSameValue
        ? moment(val).startOf('day').format('YYYY-MM-DD')
        : moment(val).toISOString();
    }
    if (isTime(val)) return convertHourToSeconds(val);
    return val;
  };

  const processIntervalFilter = (_field, value, attr) => {
    if (!value.start || !value.end) return;

    const isSameValue =
      isDate(value.start) &&
      isDate(value.end) &&
      moment(value.start).isSame(moment(value.end));

    criteriaChain.criteriaChain[0].and.push(
      { gte: parseDateOrTime(value.start, isSameValue), attr },
      { lte: parseDateOrTime(value.end, isSameValue), attr }
    );
  };

  const processNormalFilter = (field, value, attr, isText, valueIsArray) => {
    const filter = { attr };
    let processedValue = value;
    if (isText && !valueIsArray) {
      filter.matchMode = getMatchMode(processedValue);
      processedValue = removeIdentifierMatchModeOfValue(processedValue);
    }
    if (field?.lookupEntity) {
      processedValue = Array.isArray(processedValue)
        ? processedValue.map((item) => item.id)
        : processedValue.id;
    }
    if (processedValue && valueIsArray) {
      filter.in = processedValue;
    } else if (processedValue) {
      filter.eq =
        field.type === FieldType.DATE
          ? moment(processedValue).format('YYYY-MM-DD')
          : processedValue;
    }
    criteriaChain.criteriaChain[0].and.push(filter);
  };

  Object.entries(filters).forEach(([key, value]) => {
    if (!Object.prototype.hasOwnProperty.call(filters, key)) return;
    if (!value || (Array.isArray(value) && value.length === 0)) {
      return;
    }

    const field = fieldsWithInterval.find((item) => item.name === key);
    if (!field) {
      return;
    }

    const isIntervalFilter = isAIntervalField(field);
    const isText = isATextValue(field, isLookupFilter);
    const attr = getAttributeKey(field);

    const valueIsArray = value instanceof Array;

    if (isIntervalFilter) {
      processIntervalFilter(field, value, attr);
    } else {
      processNormalFilter(field, value, attr, isText, valueIsArray);
    }
  });

  return criteriaChain;
};
